import { FieldArray } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { groupBy } from 'ramda';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCatalogsKnmActTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmActTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActTypeSelectOption';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { GroupedViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/GroupedViolatedRequirementsNpaItem';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { WAS_REMOVE_DUPLICATE_NPA } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';

import styles from './PmActViolations.module.scss';
import { IKnmErknmFormObject } from '../../../../../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../../../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { useShowMore } from '../../../../../../../hooks/useShowMore';

interface IPmActViolationsProps {
  id: string;
  isWatchOnly: boolean;
  name: string;
  value: IKnmPmFormAct;
  isDisabledField: boolean;
  formRef: IKnmFormRef | undefined;
  requirementsNpa: IKnmPmFormRequirementsNpa[] | undefined;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objectsPm?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
  isDisabledByDirty?: boolean;
}

interface IPmActViolationsStoreProps {
  knmActTypesOptions: IKnmActTypeSelectOption[] | undefined;
}

const PmActViolationsWithoutStore = ({
  id,
  isWatchOnly,
  name,
  value,
  formRef,
  knmActTypesOptions,
  isDisabledField,
  requirementsNpa,
  knoOrganization,
  supervisionType,
  objectsPm,
  riskIndicators,
  isDisabledByDirty = false,
}: IPmActViolationsProps & IPmActViolationsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const remove = formRef?.current.mutators.remove;
  const removeBatch = formRef?.current.mutators.removeBatch;
  const push = formRef?.current.mutators.push;
  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [initialRequirementsModal, setInitialRequirementsModal] = useState<IRequirementsNpaInitValues | undefined>();

  const requirementsFieldName = 'requirementsNpa';

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;

  const removeViolatedRequirementsNpa = (values?: IKnmErknmFormViolatedRequirementsNpa[]) => (
    npaIndex: number,
    npaId?: number,
  ) => {
    if (npaIndex !== -1) {
      remove && remove(violatedRequirementsNpaFieldName, npaIndex);
    } else if (npaId) {
      const indexes: number[] = [];

      values?.forEach((item, ind) => {
        if (item.npa.id === npaId) {
          indexes.push(ind);
        }
      });

      removeBatch && removeBatch(violatedRequirementsNpaFieldName, indexes);
    }
  };

  const removeStructuralUnit = (values?: IKnmErknmFormViolatedRequirementsNpa[]) => (structuralUnitId: number) => {
    if (structuralUnitId) {
      const indexes: number[] = [];

      values?.forEach((item, ind) => {
        if (item.structuralUnit.id === structuralUnitId) {
          indexes.push(ind);
        }
      });

      removeBatch && removeBatch(violatedRequirementsNpaFieldName, indexes);
    }
  };

  const handleIsViolatedMandatoryRequirementsChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value) {
        // @ts-ignore
        formRef?.current.change(violatedRequirementsNpaFieldName as keyof IKnmForm);
      }
    },
    [formRef, knmActTypesOptions],
  );

  const openViolatedRequirementsNpaModal = useCallback(() => {
    setInitialRequirementsModal(undefined);
    setIsRequirementsModalVisible(true);
  }, []);

  const openRequirementsModal = (initialValue: IRequirementsNpaInitValues) => {
    setInitialRequirementsModal(initialValue);
    setIsRequirementsModalVisible(true);
  };

  const addViolatedRequirementsNpa = (items: IKnmPmFormRequirementsNpa[]) => {
    // @ts-ignore
    const requirements = formRef?.current?.getFieldState(violatedRequirementsNpaFieldName)
      ?.value as IKnmErknmFormViolatedRequirementsNpa[];
    let hasFoundDuplicate = false;

    if (push) {
      items.forEach(item => {
        const newItem: IKnmErknmFormViolatedRequirementsNpa = {
          guid: item.guid,
          npa: item.npa,
          structuralUnit: item.structuralUnit,
          violatedMandatory:
            item?.mandatory?.map(e => ({
              mandatory: e,
              category: undefined,
              violations: null,
            })) || [],
          isNew: true,
        };

        if (requirements?.length) {
          const npaIndex = requirements.findIndex(
            req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
          );

          if (npaIndex > -1) {
            hasFoundDuplicate = true;
          } else {
            push(violatedRequirementsNpaFieldName, newItem);
          }
        } else {
          push(violatedRequirementsNpaFieldName, newItem);
        }
      });
    }

    if (hasFoundDuplicate) {
      dispatch(
        addNoticeAction({
          renderContent: WAS_REMOVE_DUPLICATE_NPA,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.NPA_CHECK,
        }),
      );
    }
  };

  const { events } = value;

  const isAllActionsNotPerformed: boolean = useMemo(() => {
    if (!events?.length) {
      return false;
    }

    return events.every(e => e?.actionNotPerformed);
  }, [events]);

  useEffect(() => {
    // Если в перечне осуществленных действий отсутсвуют элементы с проводимыми действиями, то раздел
    // "Выявленные нарушения обязательных требований" - программно очищаем и блокируем
    if (isAllActionsNotPerformed && value.isViolatedMandatoryRequirements?.value !== false) {
      // @ts-ignore
      formRef?.current.change(`${name}.isViolatedMandatoryRequirements`, booleanSelectOptions[1]);
      // @ts-ignore
      formRef?.current.change(violatedRequirementsNpaFieldName as keyof IKnmForm);
    } else if (!value.violatedMandatoryRequirements?.length) {
      // @ts-ignore
      formRef?.current.change(`${name}.isViolatedMandatoryRequirements`);
    }
  }, [isAllActionsNotPerformed, value.violatedMandatoryRequirements?.length, name, violatedRequirementsNpaFieldName]);

  const requirementsNpaFieldValue = formRef?.current.getFieldState(requirementsFieldName)
    ?.value as IKnmPmFormRequirementsNpa[];

  // забираем список id mandatory из блока "Предмет контрольного (надзорного) мероприятия"
  // для использования в модальном окне добавления обязательного требования в акте (нарушения)
  const mrIds = useMemo(() => {
    if (requirementsNpaFieldValue) {
      const set = new Set();

      requirementsNpaFieldValue?.forEach(el => {
        el.mandatory?.forEach(item => {
          if (item?.id) {
            set.add(item.id);
          }
        });
      });

      return Array.from(set);
    }

    return undefined;
  }, [requirementsNpaFieldValue]) as number[] | undefined;

  return (
    <div className={styles.GroupMarginBottom}>
      <FieldBlock
        id={`${id}.isViolatedMandatoryRequirements`}
        label={getTitleFieldWithRedStar({
          label: 'Выявленные нарушения обязательных требований',
          field: `${name}.isViolatedMandatoryRequirements`,
          isPm: true,
        })}
        size="half"
      >
        <Row>
          <SelectInputField
            className={`shared-row-flex ${styles.SelectInputField}`}
            name={`${name}.isViolatedMandatoryRequirements`}
            options={booleanSelectOptions}
            isClearable={false}
            disabled={isDisabledField || isAllActionsNotPerformed || isDisabledByDirty}
            onChange={handleIsViolatedMandatoryRequirementsChange}
            size="medium"
          />
          {value.isViolatedMandatoryRequirements?.value === true && (
            <Button
              className="shared-btn-add"
              text={getTitleFieldWithRedStar({
                label: 'Добавить',
                field: violatedRequirementsNpaFieldName,
                isPm: true,
              })}
              type="button"
              variant="link"
              color="primary"
              disabled={isDisabledField || isDisabledByDirty}
              onClick={openViolatedRequirementsNpaModal}
            />
          )}
        </Row>
      </FieldBlock>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = getFieldArrayErrorMessage(props.errors?.act?.violatedMandatoryRequirements);
          const isFieldTouch = props.touched?.['act.violatedMandatoryRequirements'];

          if (fieldError && isFieldTouch) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={violatedRequirementsNpaFieldName}>
        {({ fields }) => {
          const byNpaId = groupBy<IKnmErknmFormViolatedRequirementsNpa>(item => item.npa.id.toString());
          const groupedSu = fields.value ? byNpaId(fields.value) : {};
          const groupedKeys = Object.keys(groupedSu);

          const { visibleItems, hasMore, showMore, totalCount, visibleCount } = useShowMore(groupedKeys, {
            initialCount: 10,
            incrementCount: 10,
          });

          return (
            <>
              {visibleItems.map(key => {
                const item = groupedSu[key];

                return (
                  <GroupedViolatedRequirementsNpaItem
                    key={key}
                    groupNpa={item}
                    allItems={fields.value as IKnmErknmFormViolatedRequirementsNpa[]}
                    name={fields.name}
                    watchOnly={isDisabledField}
                    isDisabledByDirty={isDisabledByDirty}
                    isViolationFieldsWatchOnly={isWatchOnly}
                    onRemove={removeViolatedRequirementsNpa(fields.value as IKnmErknmFormViolatedRequirementsNpa[])}
                    onRemoveStructuralUnit={removeStructuralUnit(
                      fields.value as IKnmErknmFormViolatedRequirementsNpa[],
                    )}
                    addMandatory={openRequirementsModal}
                    formRef={formRef}
                    isMandatoryDisabled={false}
                    id={id}
                    inAct
                    isPaperless={true}
                    isPm
                  />
                );
              })}
              {hasMore && <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />}
            </>
          );
        }}
      </FieldArray>
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          inputRequirementsNpa={requirementsNpa}
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={supervisionType?.value.dictVersionId || ''}
          withRequirements={true}
          addRequirementsNpa={addViolatedRequirementsNpa}
          initValues={initialRequirementsModal}
          objects={objectsPm}
          riskIndicators={riskIndicators}
          mrIds={mrIds}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmActViolationsStoreProps => ({
  knmActTypesOptions: getCatalogsKnmActTypesSelectOptions(state),
});

export const PmActViolations = connect(mapStateToProps)(PmActViolationsWithoutStore);
